// Cloudflare Turnstile widget mount.
//
// Renders the `<cf-turnstile>` challenge container and asks the
// Turnstile JS (loaded from index.signup.html) to populate it. When
// the customer completes the challenge — usually invisibly — the
// `onVerified` callback fires with the resulting response token,
// which the caller then sends on the POST /api/public/signup/draft
// body so the API's verifyTurnstile() check passes.
//
// The site key arrives as a prop, fetched at runtime from
// GET /api/public/signup/config (Vault-backed) rather than baked in at
// build time — see ../lib/turnstile.ts for why. When it's empty (dev /
// pre-rollout), the component renders nothing and fires onVerified('')
// on mount so the caller continues with a null token. The API mirrors
// that no-op (apps/api/src/services/turnstile.ts: skip when
// signup.turnstile.secret is unset). This keeps the dev path
// zero-touch.
//
// Cleanup: removes the rendered widget on unmount so React StrictMode's
// double-mount in dev doesn't leave two widgets on the page.

import { useEffect, useRef } from 'react';
import { isTurnstileEnabled, normalizeSitekey } from '../lib/turnstile';

interface Props {
  /**
   * Site key from /api/public/signup/config. Null/empty disables the
   * widget. Must already be RESOLVED when this component mounts — the
   * disabled branch fires onVerified('') immediately, so mounting with a
   * not-yet-fetched null would create a draft with a null token and get
   * it rejected. App.tsx only renders this in the 'challenge' phase,
   * which is reached after the config fetch settles.
   */
  sitekey: string | null;
  /** Fired once Turnstile produces a token (or immediately with '' when disabled). */
  onVerified: (token: string) => void;
  /** Fired on Cloudflare error/expiry so the caller can surface a retry. */
  onError?: (reason: string) => void;
}

export function TurnstileWidget({ sitekey, onVerified, onError }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    // Disabled: no sitekey → fire empty token and render nothing.
    if (!isTurnstileEnabled(sitekey)) {
      if (!firedRef.current) {
        firedRef.current = true;
        onVerified('');
      }
      return;
    }

    // Poll briefly for the Cloudflare script to load. The <script> tag in
    // index.signup.html is `defer`, so by the time React mounts this
    // component window.turnstile is usually already present — but be
    // defensive against a slow CDN.
    let cancelled = false;
    let pollHandle: number | null = null;

    const mount = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      // StrictMode double-mount guard.
      if (widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: normalizeSitekey(sitekey),
        theme: 'light',
        callback: (token) => {
          if (cancelled) return;
          onVerified(token);
        },
        'error-callback': () => {
          if (cancelled) return;
          onError?.('turnstile-error');
        },
        'expired-callback': () => {
          if (cancelled) return;
          onError?.('turnstile-expired');
        },
      });
    };

    if (window.turnstile) {
      mount();
    } else {
      const start = Date.now();
      pollHandle = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(pollHandle!);
          mount();
        } else if (Date.now() - start > 8000) {
          // Cloudflare script never loaded — surface as an error so the
          // caller can offer a reload button rather than wedging the SPA.
          window.clearInterval(pollHandle!);
          onError?.('turnstile-script-load-timeout');
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (pollHandle !== null) window.clearInterval(pollHandle);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone — fine */
        }
        widgetIdRef.current = null;
      }
    };
    // onVerified/onError are recreated each render by the parent; we
    // intentionally don't re-mount on every change. The latest values
    // are captured via closure at mount time, which is what we want for
    // a one-shot widget. `sitekey` IS a real dependency — it arrives
    // asynchronously, so the effect must re-run when it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey]);

  if (!isTurnstileEnabled(sitekey)) return null;
  return <div ref={containerRef} className="my-4 flex justify-center" />;
}
